import { useState, type FormEvent } from "react";
import { asDecimalString } from "@shop-ledger/shared-types";
import { Button, Input, Modal } from "@shop-ledger/ui-components";
import { useAuth } from "../auth/AuthContext";
import { writeOffInventoryItem } from "../api/inventoryApi";
import type { InventoryBatchOut } from "../api/types";
import { ApiError } from "../lib/apiClient";
import { StatusBanner } from "./StatusBanner";

export interface WriteOffModalProps {
  open: boolean;
  onClose: () => void;
  itemId: string;
  itemName: string;
  currentStock: string;
  onWrittenOff: () => void;
  /** When given, shows a "which batch?" picker (InventoryDetailPage's case — the item may
   * have several). Omit to write off without naming one (draws FEFO) or when the batch is
   * already implied by context (ExpiringSoonPage's case — pass `initialBatchId` instead). */
  batches?: InventoryBatchOut[];
  /** Pre-selects (and, when `batches` isn't given, silently locks the request to) this
   * batch — ExpiringSoonPage passes the row's own batch id, since that row IS the batch
   * being written off. */
  initialBatchId?: string;
}

/**
 * `POST /inventory/{id}/write-off` — records a reasoned `EXPIRED_WASTE` `StockLog` and
 * decrements stock, instead of stock silently vanishing via an unexplained manual edit.
 * Shared by `ExpiringSoonPage` (item 6: "a UI action on a product or from the
 * expiring-soon list") and `InventoryDetailPage`.
 */
export function WriteOffModal({
  open,
  onClose,
  itemId,
  itemName,
  currentStock,
  onWrittenOff,
  batches,
  initialBatchId,
}: WriteOffModalProps) {
  const { ensureFreshToken } = useAuth();
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [batchId, setBatchId] = useState(initialBatchId ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await writeOffInventoryItem(token, itemId, {
        quantity: asDecimalString(quantity),
        reason,
        batch_id: batchId || initialBatchId || undefined,
      });
      setQuantity("");
      setReason("");
      setBatchId(initialBatchId ?? "");
      onWrittenOff();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not record write-off.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Write off — ${itemName}`}>
      <p className="muted">Current stock: {currentStock}. Recorded as an EXPIRED_WASTE stock-log entry with your reason attached — never a silent adjustment.</p>
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <Input label="Quantity to write off" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
          <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. expired, damaged in transit" required />
          {batches && batches.length > 0 ? (
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="writeoff-batch">
                Batch
              </label>
              <select id="writeoff-batch" className="sl-input" value={batchId} onChange={(e) => setBatchId(e.target.value)}>
                <option value="">Auto (oldest-expiring first)</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.batch_number ?? "Unspecified batch"} {b.expiry_date ? `— expires ${b.expiry_date}` : ""} ({b.quantity_remaining}{" "}
                    left)
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
        {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}
        <div className="form-actions">
          <Button type="submit" variant="danger" disabled={submitting}>
            {submitting ? "Recording…" : "Confirm write-off"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
